<template>
  <div :class="t.wrapper">
    <label :class="t.label">
      {{ question.prompt }}
      <span v-if="!question.required" class="text-gray-400 font-normal">(optional)</span>
    </label>
    <p
      v-if="question.subtext"
      :class="t.subtext"
      v-html="question.subtext"
    ></p>
    <input
      v-model="displayValue"
      type="tel"
      inputmode="tel"
      autocomplete="tel"
      :class="[t.input, error ? 'border-red-500' : 'border-gray-300']"
      :style="inputFocusStyle"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleInput"
      placeholder="(555) 123-4567"
    />
    <p v-if="error" :class="t.error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { tokensFor } from './size_tokens';

export default {
  name: 'QuestionPhone',
  props: {
    question: {
      type: Object,
      required: true,
    },
    existingAnswer: {
      type: Object,
      default: null,
    },
    size: {
      type: String,
      default: 'kiosk',
      validator: (v) => ['kiosk', 'form'].includes(v),
    },
  },
  data() {
    return {
      // Raw digits only (what gets saved)
      answer: '',
      // Formatted display value
      displayValue: '',
      originalAnswer: '',
      error: false,
      errorMessage: 'Please enter a valid phone number.',
      isFocused: false,
    };
  },
  computed: {
    t() {
      return tokensFor(this.size);
    },
    inputFocusStyle() {
      if (this.isFocused) {
        return {
          borderColor: 'var(--team-primary)',
          boxShadow: '0 0 0 3px rgba(var(--team-primary-rgb, 55, 65, 81), 0.1)',
        };
      }
      return {};
    },
    dirty() {
      return this.answer !== this.originalAnswer;
    },
    valid() {
      if (this.question.required) {
        return this.answer.length === 10;
      }
      // If not required, empty is valid, but if filled must be 10 digits
      if (this.answer.length === 0) return true;
      return this.answer.length === 10;
    },
  },
  watch: {
    answer() {
      this.emit();
      if (this.valid) {
        this.error = false;
      }
    },
  },
  created() {
    if (this.existingAnswer && this.existingAnswer.answer) {
      this.answer = this.existingAnswer.answer;
      this.originalAnswer = this.existingAnswer.answer;
      this.displayValue = this.formatPhone(this.answer);
    }
    this.$notificationManager?.on('show-reg-errors', this.validate);
  },
  destroyed() {
    this.$notificationManager?.off('show-reg-errors', this.validate);
  },
  methods: {
    handleInput(event) {
      // Extract only digits
      const digits = event.target.value.replace(/\D/g, '');
      // Limit to 10 digits
      this.answer = digits.slice(0, 10);
      // Update display with formatting
      this.displayValue = this.formatPhone(this.answer);
    },
    formatPhone(digits) {
      if (!digits) return '';
      if (digits.length <= 3) {
        return `(${digits}`;
      }
      if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      }
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    },
    emit() {
      this.$emit('answer-changed', this.question, this.answer, this.dirty, this.valid);
    },
    validate() {
      if (!this.valid) {
        this.error = true;
        if (this.question.required && this.answer.length === 0) {
          this.errorMessage = 'Please enter a phone number.';
        } else {
          this.errorMessage = 'Please enter a valid 10-digit phone number.';
        }
      } else {
        this.error = false;
      }
    },
    handleFocus() {
      this.isFocused = true;
    },
    handleBlur() {
      this.isFocused = false;
      this.validate();
    },
  },
};
</script>
